import { useCallback } from 'react'
import { useCreditUsage } from 'hooks/useCreditUsage'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { usePurchaseContentCreditsModal } from 'hooks/usePurchaseContentCreditsModal'
import { useRootStore } from 'stores/RootStore'
import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'

/**
 * Dispatch a credit-gated action with a dynamic charge. Use this when the
 * charge isn't known at hook-call time (e.g. it depends on form values).
 * If the user lacks credits, opens the purchase modal; otherwise fires the
 * action directly.
 */
export const useCreditsGateDispatch = () => {
  const { creditsRemaining, canAutoReload } = useCreditUsage()
  const openPurchaseModal = usePurchaseContentCreditsModal()
  const workspace = useCurrentWorkspace()
  const {
    tierUsageStore: { autopilotAddOns },
  } = useRootStore()

  const autopilotContentCreditsPrice = autopilotAddOns.find(
    ({ key }) => key === PaymentPlanKey.BlazeAutopilotContentCredits,
  )?.prices[0]
  const autoReloadBudgetReached = Boolean(workspace?.auto_reload_credits && !canAutoReload)

  return useCallback(
    (charge: number, action: () => void) => {
      // A used-up auto-reload budget only matters when the balance itself falls short; with
      // enough credits in the bank the action must go ahead. (BLZ-16562)
      if (autoReloadBudgetReached && charge > creditsRemaining && autopilotContentCreditsPrice) {
        openPurchaseModal(
          {
            price: autopilotContentCreditsPrice,
            defaultBatchCount: Math.max(1, Math.ceil(Math.max(charge, 100) / 100)),
            onSuccess: action,
          },
          { newStack: true },
        )
        return
      }
      if (charge > creditsRemaining && autopilotContentCreditsPrice) {
        openPurchaseModal(
          {
            price: autopilotContentCreditsPrice,
            defaultBatchCount: Math.ceil((charge - creditsRemaining) / 100),
            onSuccess: action,
          },
          { newStack: true },
        )
        return
      }
      action()
    },
    [autoReloadBudgetReached, autopilotContentCreditsPrice, creditsRemaining, openPurchaseModal],
  )
}

/**
 * Static-charge variant: pass `charge` at hook-call time and receive a
 * callback that gates the action. Delegates to `useCreditsGateDispatch`.
 */
export const useCreditsGate = <TArgs extends unknown[]>(
  charge: number,
  action?: (...args: TArgs) => void | Promise<void>,
) => {
  const dispatch = useCreditsGateDispatch()

  return useCallback(
    async (...args: TArgs) => {
      dispatch(charge, () => {
        void action?.(...args)
      })
    },
    [dispatch, charge, action],
  )
}
